import logo from "./assets/ruck-logo-clear-bg.png";
import { config } from "../config";
import { toLocalISOString } from "../utils/formatters";
import DeliveryAddressForm from "./DeliveryAddressForm";
import "./Shipping.css";

export const generateDateOptions = (
  storeSchedule,
  readyToPrepTime,
  minutesToPrepareOrder,
  minutesToLoadAndDeliver,
  shippingMethod
) => {
  const absoluteEarliestTime = new Date(
    readyToPrepTime?.getTime() +
    minutesToPrepareOrder * 60000 +
    (shippingMethod === "deliver" ? minutesToLoadAndDeliver * 60000 : 0)
  );
  
  // Extract weekdays with availability
  const availableWeekdays = storeSchedule
    .filter((day) => JSON.parse(day.schedule).length > 0) // Days with open hours
    .map((day) => day.weekday.toLowerCase());

  const dates = [];
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  if (shippingMethod === "deliver") {
    date.setDate(date.getDate() + 1); // Same-day delivery is handled separately
  }

  const earliestDate = new Date(absoluteEarliestTime);
  earliestDate.setHours(0, 0, 0, 0);

  let daysChecked = 0;
  let elapsedBusinessMinutes = 0;
  const MAX_DAYS_TO_CHECK = 90;
  
  while (dates.length < 30 && daysChecked < MAX_DAYS_TO_CHECK) {
    daysChecked++;
    
    // Skip dates before the absolute earliest time
    if (date >= earliestDate) {
      const weekdayName = date
        .toLocaleString("en-US", { weekday: "long" })
        .toLowerCase();
      if (availableWeekdays.includes(weekdayName)) {
        // Get the store's schedule for this day
        const daySchedule = storeSchedule.find(
          (s) => s.weekday.toLowerCase() === weekdayName
        );
        if (daySchedule && daySchedule.schedule) {
          try {
            const periods = JSON.parse(daySchedule.schedule);
            if (periods && periods.length > 0) {
              // Check if any time slot on this day would be after the absolute earliest time
              const latestCloseTime = new Date(date);
              const [hours, minutes] = periods[periods.length - 1].close.split(":");
              latestCloseTime.setHours(hours, minutes, 0, 0);
              
              // Only include the date if the store's closing time is after the absolute earliest time
              if (latestCloseTime > absoluteEarliestTime) {
                // Generate time options for this date
                const dateStr = toLocalISOString(date);
                const result = generateTimeOptionsForDate(
                  storeSchedule,
                  dateStr,
                  readyToPrepTime,
                  minutesToPrepareOrder,
                  minutesToLoadAndDeliver,
                  elapsedBusinessMinutes,
                );
                
                // Update the elapsed minutes for the next day
                elapsedBusinessMinutes = result.elapsedBusinessMinutes;
                                  
                dates.push({
                  date: dateStr,
                  formattedDate: date.toLocaleDateString("en-US", {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                  }),
                  timeOptions: result.timeOptions
                });
              }
            }
          } catch (error) {
            console.error("Error parsing store schedule:", error);
          }
        }
      }
    }
    // Move to next day
    date.setDate(date.getDate() + 1);
    date.setHours(0, 0, 0, 0);
  }
  return dates;
};

const roundToNext15 = (time) => {
  const minutes = time.getMinutes();
  const roundedMinutes = Math.ceil(minutes / 15) * 15;
  time.setMinutes(roundedMinutes, 0, 0); // Set rounded minutes and reset seconds/milliseconds
  return time;
}

const generateTimeOptionsForDate = (
  storeSchedule,
  deliveryDate,
  readyToPrepTime,
  minutesToPrepareOrder,
  minutesToLoadAndDeliver,
  currentElapsedMinutes,
) => {
  if (!deliveryDate || !storeSchedule) return { timeOptions: [], elapsedBusinessMinutes: currentElapsedMinutes };

  const weekday = new Date(deliveryDate + 'T12:00:00')
    .toLocaleString("en-us", { weekday: "long" })
    .toLowerCase();
  const daySchedule = storeSchedule.find((s) => s.weekday === weekday);

  if (!daySchedule || !daySchedule.schedule) return { timeOptions: [], elapsedBusinessMinutes: currentElapsedMinutes };

  const periods = JSON.parse(daySchedule.schedule);
  const timeOptions = [];
  
  let elapsedBusinessMinutes = currentElapsedMinutes;

  let startTime = new Date(`${deliveryDate}T${periods[0].open}-07:00`);  // assume store is in MT timezone
  if (startTime < readyToPrepTime) {
    startTime = new Date(readyToPrepTime);
  }

  // Loop through each open-close period and generate 15-minute intervals
  periods.forEach((period) => {
    const openTime = new Date(`${deliveryDate}T${period.open}`);
    const closeTime = new Date(`${deliveryDate}T${period.close}`);
    let timeOption = startTime > openTime ? startTime : openTime;

    // Round timeOption up to the nearest 15-minute mark
    timeOption = roundToNext15(timeOption);

    while (timeOption <= closeTime) {
      // Add load and deliver time (will be 0 for a pick-up order)
      const finalTime = new Date(timeOption.getTime() + minutesToLoadAndDeliver * 60000);
      const roundedFinalTime = roundToNext15(finalTime);

      if (elapsedBusinessMinutes < minutesToPrepareOrder) { // Order not ready yet
      } else if (roundedFinalTime.getHours() < timeOption.getHours()) { // Would require overnight delivery
      } else {
        timeOptions.push(
          roundedFinalTime.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })
        );
      }
      timeOption.setMinutes(timeOption.getMinutes() + 15);
      elapsedBusinessMinutes += 15;
    }
  });

  return { timeOptions, elapsedBusinessMinutes };
};

function Shipping({
  store,
  shippingMethod,
  setShippingMethod,
  onProceed,
  onBack,
  theme,
  discount,
  total,
  setDeliveryFee,
  deliveryFee,
  deliveryOption,
  setDeliveryOption,
  productTotal,
  deliveryDate,
  setDeliveryDate,
  deliveryTime,
  setDeliveryTime,
  deliveryAddress,
  setDeliveryAddress,
  deliveryCity,
  setDeliveryCity,
  deliveryState,
  setDeliveryState,
  deliveryZip,
  setDeliveryZip,
  pickupTime,
  setPickupTime,
  pickupDate,
  setPickupDate,
  deliveryOptions,
  setDeliveryOptions,
  setTotalDriveTime,
  storeSchedule,
  optionsReceived,
  setOptionsReceived,
  tax,
  setFormattedDeliveryAddress,
  cart,
  routeDetails,
  setRouteDetails,
  availableDateOptions,
}) {
  const selectedDateOption = availableDateOptions.find(
    (option) => option.date === deliveryDate
  );

  const handleShippingChange = (method) => {
    setShippingMethod(method);

    if (method === "deliver") {
      setDeliveryOption((prev) => {
        if (deliveryOptions.includes("asap")) {
          return prev || "asap"; // Use previous selection or default to 'asap'
        }
        return "schedule"; // Default to 'schedule' if 'asap' is not available
      });
    } else {
      setDeliveryFee(0.0);
    }
  };

  const fetchDeliveryOptions = async () => {
    setOptionsReceived(false);
    if (deliveryAddress === "" || deliveryCity === "" || deliveryState === "" || deliveryZip === "") {
      alert("Please fill out the delivery address, city, state, and zip code.");
      return;
    }

    try {
      const response = await fetch(
        `${config.apiUrl}/get-delivery-options`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            store: store,
            delivery: {
              address: deliveryAddress,
              city: deliveryCity,
              state: deliveryState,
              zip: deliveryZip,
            },
            schedule: storeSchedule,
            cart: cart,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        const options = data.data.options || [];

        setOptionsReceived(true);
        setDeliveryOptions(options);
        setRouteDetails(data.data.route);
        setFormattedDeliveryAddress(data.data.formatted_delivery_address);

        // Extract total-drive-time and delivery-deadline
        const driveTimeMinutes = data.data.route["total-drive-time"];
        setTotalDriveTime(driveTimeMinutes);

        if (options.includes("asap")) {
          setDeliveryOption("asap");
          setDeliveryFee(data.data.delivery_fee_total);
        } else if (options.includes("schedule")) {
          setDeliveryOption("schedule");
          setDeliveryFee(data.data.delivery_fee_total);
        } else {
          setDeliveryOption(null);
          setDeliveryFee(0.0);
        }

        setDeliveryDate(null);
        setDeliveryTime(null);
        
      } else {
        alert("Failed to fetch delivery options. Please try again.");
      }
    } catch (error) {
      console.error("Error fetching delivery options:", error);
      alert("An error occurred while fetching delivery options.");
    }
  };

  const isNextButtonEnabled = () => {
    if (shippingMethod === "deliver") {
      if (deliveryOption === "asap") {
        return deliveryAddress && deliveryCity && deliveryState && deliveryZip;
      }
      return (
        deliveryDate &&
        deliveryTime &&
        deliveryAddress &&
        deliveryCity &&
        deliveryState &&
        deliveryZip
      );
    } else if (shippingMethod === "pickup") {
      // Check if pickupDate and pickupTime are populated
      return pickupDate && pickupTime;
    }
    return false;
  };

  const boxStyles = (selected) => ({
    flex: 1,
    border: `1px solid ${selected ? theme.ruckYellow : "transparent"}`,
    borderRadius: "10px",
    padding: "10px",
    cursor: "pointer",
    backgroundColor: selected ? theme.ruckYellow : "#D8D8D8",
    boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
    textAlign: "center",
    color: selected ? "white" : "#333",
    fontWeight: "600",
    fontSize: 14,
  });

  const styles = {
    title: { color: theme.primaryFontColor, fontSize: "20px", marginBottom: "20px" },
    button: {
      backgroundColor: theme.accentColor,
      color: "white",
      border: "none",
      padding: "10px 15px",
      borderRadius: "5px",
      cursor: "pointer",
      width: 150,
      opacity: isNextButtonEnabled() ? 1 : 0.5,
      pointerEvents: isNextButtonEnabled() ? "auto" : "none",
    },
    label: {
      display: "block",
      marginBottom: "10px",
      color: "black",
      fontSize: 14,
      fontWeight: "500",
    },
    footer: {
      position: "absolute",
      bottom: "0",
      left: "0",
      right: "0",
      padding: "10px 20px",
      display: "flex",
      justifyContent: "space-between",
      backgroundColor: "white",
      borderTop: "1px solid #ccc",
      borderBottomLeftRadius: "10px",
      borderBottomRightRadius: "10px",
    },
    subtotal: {
      color: theme.primaryFontColor,
      // marginRight: 20,
      fontSize: 14,
      // fontWeight: "bold",
      marginTop: -7,
    },
    shipping: {
      color: "black",
      fontSize: 14,
      marginTop: -7,
    },
    input: {
      display: "block",
      width: "100%",
      marginBottom: "10px",
      padding: "7px",
      border: "1px solid #ccc",
      borderRadius: "5px",
    },
  };

  return (
    <div
      className="shipping-container"
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <div className="shipping-header" style={{ display: "flex", alignItems: "center" }}>
        <h3 className="shipping-title" style={styles.title}>Delivery Options</h3>
        <a
          href="https://www.ruckdelivery.com"
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: "none" }} // Optional: Remove underline from the link
        >
          <div
            className="shipping-logo"
            style={{
              backgroundColor: theme.ruckYellow,
              textAlign: "center",
              padding: 10,
              borderRadius: 5,
              fontWeight: "600",
              fontSize: 12,
              alignItems: "center",
              marginLeft: 15,
              color: "black",
              cursor: "pointer", // Ensures it looks clickable
            }}
          >
            Powered by
            <img
              src={logo}
              alt="Store Logo"
              style={{
                height: 13,
                objectFit: "contain",
                marginLeft: 5,
                marginBottom: -2,
              }}
            />
          </div>
        </a>
      </div>
      <div
        name="divider"
        style={{
          backgroundColor: theme.primaryFontColor,
          height: 1,
          marginBottom: 20,
        }}
      />
      {/* Scrollable Content Section */}
      <div className="shipping-content" style={{ flex: 1, overflowY: "auto" }}>
        <div
          className="shipping-body"
          style={{
            display: "flex",
            flexDirection: "column",
            marginTop: "10px",
            alignItems: "center",
            marginBottom: "30px",
            // backgroundColor: "red",
            // height: "100%",
          }}
        >
          <div
            className="shipping-methods"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginBottom: 20,
              width: "50%",
              border: `1px solid ${theme.ruckYellow}`,
              borderRadius: "3px",
              overflow: "hidden",
              fontSize: "12px",
            }}
          >
            {store.has_delivery && (
              <div
                onClick={() => {
                  handleShippingChange("deliver");
                  setShippingMethod("deliver");
                  setPickupDate(null);
                  setPickupTime(null);
                }}
                style={{
                  flex: 1,
                  padding: "5px",
                  textAlign: "center",
                  cursor: "pointer",
                  backgroundColor:
                    shippingMethod === "deliver" ? theme.ruckYellow : "white",
                  color: "black",
                  fontWeight: "bold",
                }}
              >
                Deliver to Site
              </div>
            )}
            <div
              onClick={() => {
                handleShippingChange("pickup");
                setDeliveryOption(null);
                setDeliveryDate(null);
                setDeliveryTime(null);
                const fee = 0.0;
                setDeliveryFee(fee);
              }}
              style={{
                flex: 1,
                padding: "5px",
                textAlign: "center",
                cursor: "pointer",
                backgroundColor:
                  shippingMethod === "pickup" ? theme.ruckYellow : "white",
                color: "black",
                fontWeight: "bold",
              }}
            >
              Pick Up Myself
            </div>
          </div>

          <div>
            {shippingMethod === "deliver" ? (
              <div
                className="shipping-deliver"
                style={{
                  width: "90%",
                  justifySelf: "center",
                }}>
                <div
                  className="shipping-deliver-body responsive-container"
                  style={{
                    flexDirection: "column",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 20,
                    // width: "100%",
                    // margin: "0 auto",
                  }}
                >
                  <div
                    className="shipping-deliver-address responsive-child"
                    style={{
                      flex: 1,
                    }}
                  >
                    <DeliveryAddressForm
                      deliveryAddress={deliveryAddress}
                      setDeliveryAddress={setDeliveryAddress}
                      deliveryCity={deliveryCity}
                      setDeliveryCity={setDeliveryCity}
                      deliveryState={deliveryState}
                      setDeliveryState={setDeliveryState}
                      deliveryZip={deliveryZip}
                      setDeliveryZip={setDeliveryZip}
                      theme={theme}
                    />
                  </div>
                  <button
                    className="shipping-deliver-button responsive-button"
                    onClick={fetchDeliveryOptions}
                    style={{
                      backgroundColor: theme.accentColor,
                      color: "white",
                      border: "none",
                      padding: "10px 15px",
                      borderRadius: "5px",
                      cursor:
                        deliveryAddress &&
                        deliveryCity &&
                        deliveryState &&
                        deliveryZip
                          ? "pointer"
                          : "not-allowed",
                      opacity:
                        deliveryAddress &&
                        deliveryCity &&
                        deliveryState &&
                        deliveryZip
                          ? 1
                          : 0.5,
                      width: 170,
                      fontWeight: "600",
                      marginLeft: 10, 
                      marginTop: 10,
                    }}
                    disabled={
                      deliveryAddress === "" ||
                      deliveryCity === "" ||
                      deliveryState === "" ||
                      deliveryZip === ""
                    }
                  >
                    Get Delivery Options
                  </button>
                </div>

                {deliveryOptions.length > 0 && optionsReceived === true ? (
                  <div
                    className="shipping-deliver-options"
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      gap: 20,
                    }}
                  >
                    <div
                      className="shipping-deliver-asap"
                      style={{
                        ...boxStyles(deliveryOption === "asap"),
                        cursor: deliveryOptions.includes("asap")
                          ? "pointer"
                          : "not-allowed",
                        opacity: deliveryOptions.includes("asap") ? 1 : 0.5,
                      }}
                      onClick={() => {
                        if (deliveryOptions.includes("asap")) {
                          setDeliveryOption("asap");
                          setShippingMethod("deliver");
                          setPickupTime(null);
                          setPickupDate(null);
                          setDeliveryDate(null);
                          setDeliveryTime(null);
                        }
                      }}
                    >
                      Deliver Today
                      {deliveryOptions.includes("asap") ? (
                        <div
                          style={{
                            fontSize: "14px",
                            marginTop: 10,
                          }}
                        >
                          {/* Delivers in{" "}
                          <u>
                            <strong>
                              {totalDriveTime >= 60
                                ? `${Math.floor(totalDriveTime / 60)} hr(s) ${
                                    (Math.ceil((totalDriveTime % 60) / 15) *
                                      15) %
                                    60
                                  } min(s)`
                                : `${
                                    Math.ceil(totalDriveTime / 15) * 15
                                  } minute(s)`}
                            </strong>
                          </u>
                          <br /> */}
                          Estimated Arrival:{" "}
                          <u>
                            <strong>
                              {routeDetails && routeDetails["delivery-deadline"]
                                ? routeDetails["delivery-deadline"]
                                : "N/A"}
                            </strong>
                          </u>
                        </div>
                      ) : (
                        <div
                          style={{
                            fontSize: "12px",
                            marginTop: 10,
                            color: "gray",
                          }}
                        >
                          Same-day delivery is not available for this order.
                        </div>
                      )}
                    </div>
                    {deliveryOptions.includes("schedule") && (
                      <div
                        className="shipping-deliver-schedule"
                        style={boxStyles(deliveryOption === "schedule")}
                        onClick={() => {
                          setDeliveryOption("schedule");
                          setShippingMethod("deliver");
                          setPickupTime(null);
                          setPickupDate(null);
                        }}
                      >
                        Schedule a Ruck Delivery
                        <div
                          style={{
                            display: "flex",
                            gap: "10px",
                            flexDirection: window.innerWidth < 450 ? "column" : "row",
                            justifyContent: "center",
                            marginTop: 0,
                            width: "100%", // Ensure full width for proper alignment
                            alignItems: "center",
                          }}
                        >
                          <label
                            style={{
                              fontSize: "12px",
                              fontWeight: "bold",
                              flexDirection: "column",
                            }}
                          >
                            <select
                              value={deliveryDate || ""}
                              onChange={(e) => {
                                const selectedDate = e.target.value;
                                if (selectedDate) {
                                  setDeliveryDate(selectedDate);
                                  setPickupDate(null);
                                }
                              }}
                              style={{
                                marginTop: "5px",
                                padding: "8px",
                                borderColor: "transparent",
                                borderRadius: "5px",
                                fontSize: "12px",
                                color: "black",
                                backgroundColor: "white",
                                height: 35,
                              }}
                            >
                              <option value="" disabled>
                                Select Date
                              </option>
                              {availableDateOptions.map(
                                (option) => (
                                  option.timeOptions?.length > 0 && (
                                    <option key={option.date} value={option.date}>
                                      {option.formattedDate}
                                    </option>
                                  )
                                )
                              )}
                            </select>
                          </label>
                          <label
                            style={{
                              fontSize: "12px",
                              fontWeight: "bold",
                              flexDirection: "column",
                            }}
                          >
                            {deliveryDate ? (
                              <select
                                value={deliveryTime || ""}
                                onChange={(e) => {
                                  setDeliveryTime(e.target.value);
                                }}
                                style={{
                                  marginTop: "5px",
                                  padding: "8px",
                                  borderColor: "transparent",
                                  borderRadius: "5px",
                                  fontSize: "12px",
                                  color: "black",
                                  height: 35,
                                  backgroundColor: "white",
                                }}
                              >
                                <option value="" disabled>
                                  Select Time
                                </option>
                                {selectedDateOption?.timeOptions?.map((time) => (
                                  <option key={time} value={time}>
                                    {time}
                                  </option>
                                ))}
                              </select>
                            ) : null}
                          </label>
                        </div>
                        <p style={{ fontSize: 10, margin: 0, marginTop: 7 }}>
                          You will receive a confirmation call.
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  optionsReceived === true && (
                    <div
                      style={{
                        color: theme.primaryFontColor,
                        fontWeight: "bold",
                        paddingTop: 100,
                        width: "80%",
                        textAlign: "center",
                        justifySelf: "center",
                      }}
                    >
                      The delivery cannot be scheduled online at this time. <br /> <br />
                      Please contact us at <a href={`tel:3853328011`} style={{ color: theme.primaryFontColor }}>
                        (385) 332-8011
                      </a> to proceed with scheduling this order.
                      <br />
                    </div>
                  )
                )}
              </div>
            ) : (
              <div
                className="shipping-pickup"
                style={{
                  display: "flex",
                  // alignItems: "center",
                  justifyContent: "space-between",
                  // backgroundColor: "red",
                  maxWidth: 400,
                  paddingTop: 10,
                  // flexDirection: "row",
                }}
              >
                {shippingMethod === "pickup" && (
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <div
                      style={{
                        color: "black",
                        fontSize: 13,
                        marginTop: -20,
                        marginBottom: 15,
                      }}
                    >
                      Select a day and time for pickup.
                      <br />
                      <strong>
                        THIS DOES NOT GUARANTEE THAT YOUR ORDER WILL BE READY BY
                        THE SELECTED TIME.
                      </strong>
                    </div>
                    <>
                      <label
                        style={{
                          ...styles.label,
                          marginBottom: 5,
                          fontSize: "12px",
                          fontWeight: "bold",
                        }}
                      >
                        {/* <input
                          type="date"
                          value={pickupDate || ""}
                          min={getMinDate()}
                          onChange={(e) => {
                            setDeliveryDate(null);
                            setPickupDate(e.target.value);
                          }}
                          style={{
                            display: "block",
                            marginTop: "5px",
                            padding: 3,
                            border: `1px solid ${theme.accentColor}`,
                            borderRadius: "5px",
                            fontSize: "12px",
                            color: theme.accentColor,
                            backgroundColor: "white",
                            width: "100%",
                            // padding: 10,
                            height: 30,
                          }}
                        /> */}

                        <select
                          value={pickupDate || ""}
                          onChange={(e) => {
                            const selectedDate = e.target.value;
                            if (selectedDate) {
                              setDeliveryDate(null);
                              setPickupDate(e.target.value);
                            }
                          }}
                          style={{
                            display: "block",
                            marginTop: "5px",
                            padding: 3,
                            border: `1px solid ${theme.primaryFontColor}`,
                            borderRadius: "5px",
                            fontSize: "12px",
                            color: theme.primaryFontColor,
                            backgroundColor: "white",
                            width: "100%",
                            // padding: 10,
                            height: 30,
                          }}
                        >
                          <option value="" disabled>
                            Select Date
                          </option>
                          {availableDateOptions.map((option) => (
                            <option key={option.date} value={option.date}>
                              {option.formattedDate}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label
                        style={{
                          ...styles.label,
                          marginBottom: "10px",
                          fontSize: "12px",
                          fontWeight: "bold",
                        }}
                      >
                        {/* <input
                          type="time"
                          value={pickupTime || ""}
                          onChange={(e) => {
                            setDeliveryTime(null);
                            setPickupTime(e.target.value);
                          }}
                          style={{
                            display: "block",
                            marginTop: "5px",
                            padding: 3,
                            border: `1px solid ${theme.accentColor}`,
                            borderRadius: "5px",
                            fontSize: "12px",
                            color: theme.accentColor,
                            backgroundColor: "white",
                            width: "100%",
                            // padding: 9.2,
                            height: 30,
                          }}
                        /> */}

                        {pickupDate ? (
                          <select
                            value={pickupTime || ""}
                            onChange={(e) => setPickupTime(e.target.value)}
                            style={{
                              display: "block",
                              marginTop: "5px",
                              padding: 3,
                              border: `1px solid ${theme.primaryFontColor}`,
                              borderRadius: "5px",
                              fontSize: "12px",
                              color: theme.primaryFontColor,
                              backgroundColor: "white",
                              width: "100%",
                              // padding: 9.2,
                              height: 30,
                            }}
                          >
                            <option value="" disabled>
                              Select Time
                            </option>
                            {availableDateOptions.find(option => option.date === pickupDate)?.timeOptions?.map((time) => (
                              <option key={time} value={time}>
                                {time}
                              </option>
                            ))}
                          </select>
                        ) : null}
                      </label>
                      <div
                        style={{
                          color: "black",
                          fontSize: 13,
                          // marginTop: -20,
                          marginBottom: 15,
                        }}
                      >
                        A team member will call you during business hours to
                        confirm the pickup details.
                      </div>
                    </>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        className="shipping-subtotal"
        style={{
          display: "flex",
          alignSelf: "flex-end",
          flexDirection: "column",
          width: 250,
          marginBottom: 100,
          // backgroundColor: "red",
          paddingTop: 10,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <p
            style={{
              ...styles.subtotal,
              textAlign: "left",
              flex: 1,
              color: "black",
            }}
          >
            Cart Total:
          </p>
          <p style={{ ...styles.subtotal, textAlign: "right", flex: 1 }}>
            <strong style={{ color: theme.primaryFontColor }}>
              ${(productTotal + tax - discount)?.toFixed(2) || "0.00"}
            </strong>
          </p>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <p
            style={{
              ...styles.subtotal,
              textAlign: "left",
              flex: 1,
              color: "black",
            }}
          >
            Delivery Fee:
          </p>
          <p style={{ ...styles.subtotal, textAlign: "right", flex: 1 }}>
            <strong style={{ color: theme.primaryFontColor }}>
              ${deliveryFee.toFixed(2)}
            </strong>
          </p>
        </div>
      </div>

      {/* Footer Section */}
      <div className="shipping-footer" style={styles.footer}>
        <div style={{ flex: 2, textAlign: "right" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              justifySelf: "flex-end",
            }}
          >
            <div
              style={{
                width: "35%",
              }}
            />
            <h4
              style={{
                color: "black",
                marginBottom: "10px",
                fontWeight: "400",
              }}
            >
              Order Total:
            </h4>
            <h4
              style={{
                color: theme.primaryFontColor,
                marginBottom: "10px",
                textAlign: "right",
              }}
            >
              ${total.toFixed(2)}
            </h4>
          </div>
          <div
            style={{
              // backgroundColor: "red",
              // width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "10px",
            }}
          >
            <button
              className="shipping-back-button"
              style={{ ...styles.button, opacity: 1, pointerEvents: "auto" }}
              onClick={onBack}
            >
              Back
            </button>

            <button
              className="shipping-next-button"
              style={styles.button}
              onClick={onProceed}
              disabled={!isNextButtonEnabled()}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Shipping;
